import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation } from 'react-router'
import { z } from 'zod'
import { Button, Input } from '@/design-system'
import { authenticatedHomePath, runtimeConfig } from '@/config/env'
import { AuthFailure } from '../domain/auth'
import { loginDestination } from '../domain/authorization'
import { AuthLayout } from './AuthLayout'
import { useAuth } from './useAuth'

const loginSchema = z.object({
  email: z.email('Saisissez un courriel valide.'),
  password: z.string().min(1, 'Saisissez votre mot de passe.'),
})
type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { session, loading, signIn } = useAuth()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string>()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) })
  const routeState: unknown = location.state
  const requested =
    typeof routeState === 'object' && routeState && 'from' in routeState ? String(routeState.from) : null
  const safeDestination = loginDestination(session, requested, authenticatedHomePath)

  if (!loading && session) return <Navigate to={safeDestination} replace />

  const submit = handleSubmit(async (values) => {
    setFormError(undefined)
    try {
      await signIn(values)
      // La redirection réactive ci-dessus utilise la session fraîche (compte terrain → son interface) ;
      // `session` est encore périmée dans cette fermeture, on n'y navigue donc pas ici.
    } catch (error) {
      setFormError(
        error instanceof AuthFailure ? error.message : 'La connexion est indisponible pour le moment.',
      )
    }
  })

  return (
    <AuthLayout
      aside={
        runtimeConfig.dataSource === 'fixtures' ? (
          <aside className="auth-demo" aria-label="Comptes de démonstration">
            <strong>Mode démonstration</strong>
            <span>Admin : admin@reca.local</span>
            <span>Lecture : viewer@reca.local</span>
            <span>Mot de passe : DemoReca2026!</span>
          </aside>
        ) : undefined
      }
    >
      <div className="auth-card__heading">
        <span className="auth-page__eyebrow">Bienvenue</span>
        <h2>Connexion à RECA</h2>
        <p>Utilisez votre compte professionnel pour continuer.</p>
      </div>
      <form
        className="auth-form"
        onSubmit={(event) => {
          void submit(event)
        }}
      >
        <Input
          label="Identifiant de connexion"
          type="email"
          autoComplete="email"
          placeholder="f.nom@groupereca.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <div className="auth-password-field">
          <Input
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />
          <button
            type="button"
            className="auth-password-field__toggle"
            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            onClick={() => {
              setShowPassword((visible) => !visible)
            }}
          >
            {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        </div>
        <div className="auth-form__options">
          <span>Session protégée et renouvelée automatiquement</span>
        </div>
        {formError ? (
          <div className="auth-form__error" role="alert">
            {formError}
          </div>
        ) : null}
        <Button type="submit" variant="primary" size="large" loading={isSubmitting} iconEnd={<ArrowRight />}>
          Se connecter
        </Button>
      </form>
    </AuthLayout>
  )
}
